'use client';

import { useIntl } from 'react-intl';
import { useAuthQuery } from '@packages/shared/src/hooks/useAuthQuery/useAuthQuery';
import { CustomerInvoicesDocument } from '@packages/gql/generated/shopping/CustomerInvoicesDocument';
import { Skeleton } from '@packages/shared/src/ui/Skeleton/Skeleton';
import { Button } from '@packages/shared/src/ui/Button/Button';

import { Notification } from '@packages/shared/src/ui/Notification/Notification';
import type { InvoiceListProps } from './types';
import { INVOICE_STATUS_MAPPING } from './constants';
import { InvoiceCard } from './InvoiceCard.client';
import { OfflineNotification } from '../OfflineNotification/OfflineNotification';
import { messages } from './messages';

export const InvoiceList = ({
  tab,
  paginationKey,
  isLastPage = false,
  state,
}: InvoiceListProps) => {
  const intl = useIntl();
  const { filters } = state.getTabState(tab);

  const [{ data, error, fetching }] = useAuthQuery({
    query: CustomerInvoicesDocument,
    variables: {
      maxAgeDays: filters.maxAgeDays,
      status: INVOICE_STATUS_MAPPING[filters.status],
      paginationKey,
      locale: intl.locale,
    },
  });

  const handleLoadMore = () => {
    const nextPaginationKey = data?.customerInvoices?.nextPaginationKey;

    if (nextPaginationKey) {
      state.addPaginationKey(nextPaginationKey, tab);
    }
  };

  // explicit null catches BE error
  if (error || data === null || data?.customerInvoices === null) {
    return <OfflineNotification />;
  }

  // data undefined occurs when api not yet fired
  if (fetching || typeof data === 'undefined') {
    return (
      <div className="space-y-4">
        {Array.from(
          { length: 4 },
          (_, i) => `skeleton-${tab}-${paginationKey || 'initial'}-${i}`,
        ).map((skeletonKey) => (
          <Skeleton key={skeletonKey} className="h-32 w-full rounded-lg" />
        ))}
      </div>
    );
  }

  return (
    <div>
      <div className="space-y-1.5">
        {data.customerInvoices.invoices.length === 0 && !paginationKey && (
          <Notification severity="info" variant="outline">
            {intl.formatMessage({
              id: 'customerInvoices.noInvoicesMessage',
              defaultMessage: 'Im gewählten Zeitraum hast du keine Rechnungen.',
            })}
          </Notification>
        )}
        {data.customerInvoices.invoices.map((invoice) => (
          <InvoiceCard key={invoice.invoiceId} invoice={invoice} />
        ))}
      </div>

      {isLastPage && !!data.customerInvoices.nextPaginationKey && (
        <div className="mt-1.5 flex justify-center">
          <Button onClick={handleLoadMore}>
            {intl.formatMessage(messages.loadMoreButton)} <span className="icon-emp-load-reload" />
          </Button>
        </div>
      )}
    </div>
  );
};
